import * as THREE from 'three';
import { COLOR } from '../shared/shared';
import {
	Box_t,
	BoxDimensions,
	BoxPosition_t,
	LabelSide,
	Layout_t,
	LayoutTransform,
	PalletConfigObj,
} from 'blackbox';
import { PalletConfiguration } from 'blackbox';
import { PalletSensor } from 'libapi';
import { mergeBufferGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
export interface VisualizerProps {
	palletConfig?: PalletConfiguration;
	pConfig: PalletConfigObj;
	pallet_index: number;
	stack_index: number;
	pallet_sensors?: PalletSensor[];
	max_boxes?: number;
}

export interface BoxRenderProps extends BoxDimensions {
	labelSide?: LabelSide;
	showColor?: boolean;
	children?: React.ReactNode;
	backgroundColor?: COLOR;
	hasLabels: boolean;
}

export function getOptimizedPalletMesh(
	width: number,
	length: number,
	height: number,
	woodMaterial: THREE.MeshStandardMaterial
) {
	const palletGroup = new THREE.Group();
	let fullHeight = height;
	let [zeroX, zeroY, zeroZ] = [-width / 2, -length / 2, -fullHeight / 2];

	const plankWidth = length / 20;
	const plankHeight = (fullHeight * 4) / 5;
	const plankLength = width;

	const boardHeight = fullHeight / 5;
	const boardWidth = plankWidth * 2;

	const startY = boardWidth / 2;
	const totalGap = length * 0.3;
	const numPlanks = Math.floor((length - totalGap) / boardWidth);
	const gap = (length - numPlanks * boardWidth) / (numPlanks - 1);
	const increment = boardWidth + gap;

	// Pre-create geometries
	const plankGeometry = new THREE.BoxGeometry(
		plankLength,
		plankWidth,
		plankHeight
	);
	const crossPlankGeometry = new THREE.BoxGeometry(
		plankWidth,
		length - plankWidth,
		plankHeight
	);
	const boardGeometry = new THREE.BoxGeometry(width, boardWidth, boardHeight);

	const mergedGeometries: THREE.BufferGeometry[] = [];

	const tempMatrix = new THREE.Matrix4();

	// Helper to add a geometry at a specific position
	const addGeometryInstance = (
		baseGeometry: THREE.BufferGeometry,
		x: number,
		y: number,
		z: number
	) => {
		tempMatrix.makeTranslation(x, y, z);
		const g = baseGeometry.clone();
		g.applyMatrix4(tempMatrix);
		mergedGeometries.push(g);
	};

	// Horizontal planks on top and bottom
	addGeometryInstance(
		plankGeometry,
		zeroX + width / 2,
		zeroY + length - plankWidth / 2,
		zeroZ + plankHeight / 2
	);
	addGeometryInstance(
		plankGeometry,
		zeroX + width / 2,
		zeroY + plankWidth / 2,
		zeroZ + plankHeight / 2
	);

	// Cross planks (left and right)
	addGeometryInstance(
		crossPlankGeometry,
		zeroX + plankWidth / 2,
		zeroY + (length - plankWidth) / 2,
		zeroZ + plankHeight / 2
	);
	addGeometryInstance(
		crossPlankGeometry,
		zeroX + width - plankWidth / 2,
		zeroY + (length - plankWidth) / 2,
		zeroZ + plankHeight / 2
	);

	// Top planks (surface of the pallet)
	for (let i = 0; i < numPlanks; i++) {
		const y = zeroY + startY + increment * i;
		addGeometryInstance(
			boardGeometry,
			zeroX + width / 2,
			y,
			zeroZ + fullHeight - boardHeight / 2
		);
	}

	// Merge all into one geometry and create a mesh
	const merged = mergeBufferGeometries(mergedGeometries, false);
	const palletMesh = new THREE.Mesh(merged, woodMaterial);
	palletGroup.add(palletMesh);

	return palletGroup;
}

// Get a box's functional dimensions (including multipick)
export function getBoxDimensions(
	box: Box_t,
	multipick: number,
	multipickAlongWidth: boolean
): BoxDimensions {
	const baseWidth = box.dimensions.width;
	const baseLength = box.dimensions.length;

	let width = baseWidth;
	let length = baseLength;

	if (multipick > 0) {
		if (multipickAlongWidth) {
			width *= multipick + 1;
		} else {
			length *= multipick + 1;
		}
	}
	return { length, width, height: box.dimensions.height };
}

// Convert our terrible box position format to a proper Euler angle
export function getRotationAngle(rotated: boolean, spin: boolean) {
	let angle = rotated ? -Math.PI / 2 : 0; // Rotate 90 degrees counterclockwise instead of clockwise
	if (spin) angle += Math.PI; // 180 degrees flip
	return (angle + 2 * Math.PI) % (2 * Math.PI); // Ensure positive angle
}

// Convert our terrible box position format to an offset in mm
export function getDenormalizedPosition(
	boxPosition: BoxPosition_t,
	boxDimensions: BoxDimensions,
	palletDimensions: BoxDimensions,
	isRotated: boolean
) {
	const effectiveWidth = isRotated ? boxDimensions.length : boxDimensions.width;
	const effectiveLength = isRotated ? boxDimensions.width : boxDimensions.length;

	const x =
		boxPosition.position.x * palletDimensions.width -
		palletDimensions.width / 2 +
		effectiveWidth / 2;

	const flippedY = 1 - boxPosition.position.y;
	const y =
		flippedY * palletDimensions.length -
		palletDimensions.length / 2 -
		effectiveLength / 2;

	return { x, y, z: 0 };
}

// Convert our terrible label position to the correct box face
export const FACE_MAP: Record<number, number | null> = {
	0: null,
	1: 0,
	2: 2,
	3: 1,
	4: 3,
};

export function createArrowTexture() {
	const canvas = document.createElement('canvas');
	canvas.width = 512;
	canvas.height = 512;
	const ctx = canvas.getContext('2d');

	if (ctx) {
		// Make the canvas transparent except for the arrow
		ctx.clearRect(0, 0, canvas.width, canvas.height);

		// Set arrow properties
		const arrowColor = '#000000';
		const lineWidth = 72; // Thicker line for better visibility
		ctx.strokeStyle = arrowColor;
		ctx.fillStyle = arrowColor;
		ctx.lineWidth = lineWidth;

		// Define canvas center
		const centerX = canvas.width / 2;
		const centerY = canvas.height / 2;

		// Calculate the arrow dimensions - make it bigger
		const arrowLength = canvas.width; // Longer arrow
		const arrowheadSize = arrowLength * 0.2; // Proportional arrowhead

		// Calculate points to make the arrow span the entire face
		// Start from 1/4 of the way from the bottom edge
		const startY = centerY + arrowLength * 0.25;
		// Go to 1/4 of the way from the top edge
		const endY = centerY - arrowLength * 0.25;

		// Draw the line (shaft)
		ctx.beginPath();
		ctx.moveTo(centerX, startY);
		// Stop the line exactly at the base of the arrowhead
		ctx.lineTo(centerX, endY + arrowheadSize - 2);
		ctx.stroke();

		// Draw the arrowhead
		ctx.beginPath();
		// The base of the arrowhead
		ctx.moveTo(centerX - arrowheadSize, endY + arrowheadSize);
		// The tip of the arrow
		ctx.lineTo(centerX, endY - arrowheadSize * 0.2);
		// The other side of the arrowhead
		ctx.lineTo(centerX + arrowheadSize, endY + arrowheadSize);
		ctx.closePath(); // Close the path to fill the triangle
		ctx.fill();
	}

	const texture = new THREE.CanvasTexture(canvas);
	texture.needsUpdate = true;
	return texture;
}

export const disposeMaterials = (
	material: THREE.Material | THREE.Material[] | undefined
) => {
	if (!material) return;

	if (Array.isArray(material)) {
		material.forEach(m => m.dispose());
	} else {
		material.dispose();
	}
};

export function getSortedLayout(
	palletConfig: PalletConfigObj,
	palletId: number,
	stackId: number,
	layerIndex: number,
	bayId: number
) {
	let lLayout: Layout_t = PalletConfigObj.from(palletConfig).getSortedStackLayout(
		palletId,
		stackId,
		layerIndex,
		bayId
	);

	const configCopy = JSON.parse(JSON.stringify(palletConfig));
	configCopy.config.pallets = [configCopy.config.pallets[palletId]];

	const palletCopy = configCopy.config.pallets[0];
	palletCopy.Stack = [0];
	palletCopy.Layouts = [lLayout];

	lLayout = LayoutTransform.Transform(
		LayoutTransform.TransformType.ReflectGlobalX,
		palletCopy,
		palletConfig.config.boxes,
		0
	);
	palletCopy.Layouts = [lLayout];

	lLayout = LayoutTransform.Transform(
		LayoutTransform.TransformType.ReflectGlobalY,
		palletCopy,
		palletConfig.config.boxes,
		0
	);

	return lLayout;
}